import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { Grid, ListItemText, Typography, makeStyles } from '@material-ui/core';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import ButtonWithSpinner from '../ButtonWithSpinner';
import toastError from '../../errors/toastError';
import useQueues from '../../hooks/useQueues';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
  maxWidth: {
    width: '100%',
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({
  modalOpen,
  onClose,
  ticketid,
  onTransferSuccess,
}) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState('');
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const savingRef = useRef(false);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState('');
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => setWhatsapps(data));
      };

      // A conexão pré-selecionada tem que ser a do TICKET, não a do operador
      // logado: usar user.whatsappId movia o ticket para o número do atendente
      // que fez a transferência.
      const loadTicketWhatsapp = async () => {
        try {
          const { data } = await api.get(`/tickets/${ticketid}`);
          if (isMounted.current && data?.whatsappId) {
            setSelectedWhatsapp(data.whatsappId);
          }
        } catch (err) {
          console.warn('Erro ao carregar conexão do ticket:', err);
          if (
            isMounted.current &&
            whatsappId !== null &&
            whatsappId !== undefined
          ) {
            setSelectedWhatsapp(whatsappId);
          }
        }
      };

      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id);
      }
      fetchContacts();
      loadTicketWhatsapp();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        let list = await findAllQueues();
        // Filtrar filas com nome 'Sem Fila'
        list = list.filter(q => q.name?.toLowerCase() !== 'sem fila');
        setAllQueues(list);
      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A conexão manda na lista de destinos: só aparecem as pipelines atribuídas
  // a ela (cada fila tem exatamente uma pipeline, via Pipelines.queueId).
  // Antes daqui havia o efeito inverso — escolher a fila apontava a conexão —,
  // removido de propósito: com a lista já restrita à conexão, qualquer fila
  // escolhida é atendida por ela, e manter os dois sentidos criava laço entre
  // "fila define conexão" e "conexão filtra fila".
  //
  // Quando um usuário de destino é escolhido, as filas dele também limitam:
  // não adianta oferecer uma pipeline que a conexão atende mas o atendente não.
  const availableQueues = useMemo(() => {
    let list = allQueues;

    if (selectedUser && Array.isArray(selectedUser.queues)) {
      const userQueueIds = new Set(
        selectedUser.queues.map(q => Number(q.id))
      );
      list = list.filter(q => userQueueIds.has(Number(q.id)));
    }

    const connection = whatsapps.find(
      w => Number(w.id) === Number(selectedWhatsapp)
    );
    if (connection && Array.isArray(connection.queues)) {
      const connectionQueueIds = new Set(
        connection.queues.map(q => Number(q.id))
      );
      list = list.filter(q => connectionQueueIds.has(Number(q.id)));
    }

    return list;
  }, [allQueues, selectedUser, selectedWhatsapp, whatsapps]);

  // Se a fila escolhida deixar de ser oferecida (troca de conexão ou de
  // usuário), limpa a seleção — senão o form enviaria uma fila que a conexão
  // de destino não atende.
  useEffect(() => {
    if (!selectedQueue) return;
    const stillAvailable = availableQueues.some(
      q => Number(q.id) === Number(selectedQueue)
    );
    if (!stillAvailable) {
      setSelectedQueue('');
    }
  }, [availableQueues, selectedQueue]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get('/users/', {
            params: { searchParam },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam('');
    setSelectedUser(null);
    setSelectedQueue('');
    setSelectedWhatsapp('');
    setLoading(false);
    savingRef.current = false;
  };

  const handleSaveTicket = async e => {
    e.preventDefault();
    if (savingRef.current) return;
    if (!ticketid) {
      toastError(new Error('ID do ticket não encontrado'));
      return;
    }
    if (!selectedQueue || selectedQueue === '') {
      toastError(new Error('Selecione uma fila para transferir o ticket'));
      return;
    }
    savingRef.current = true;
    setLoading(true);
    try {
      let data = {};

      if (selectedQueue && selectedQueue !== null) {
        data.queueId = selectedQueue;
        data.status = 'pending';

        if (selectedUser) {
          data.userId = selectedUser.id;
        } else {
          data.userId = null;
        }
      }

      if (selectedWhatsapp) {
        data.whatsappId = selectedWhatsapp;
      }

      // Primeiro, atualizar o ticket
      const response = await api.put(`/tickets/${ticketid}`, data);

      // Se o ticket tem um deal associado, atualizar o pipeline do deal também
      let dealId = response.data?.dealId;

      // Se não temos dealId na resposta, buscar do ticket
      if (!dealId) {
        try {
          const ticketData = await api.get(`/tickets/${ticketid}`);
          dealId = ticketData.data?.dealId;
        } catch (err) {
          console.warn('Erro ao buscar dealId do ticket:', err);
        }
      }

      if (dealId) {
        try {
          const dealUpdateData = {
            pipelineId: response.data?.queue.pipeline.id, // Usar o ID da fila como pipelineId
            stageId: null, // Deixar o backend definir o stage padrão
            status: 'open',
          };

          const dealResponse = await api.patch(
            `/deals/${dealId}`,
            dealUpdateData
          );
        } catch (dealError) {
          console.warn(
            'Erro ao atualizar deal, mas ticket foi atualizado:',
            dealError
          );
          // Não falhar a operação se o deal não puder ser atualizado
        }
      }

      // Saudação na transferência: enviada apenas pelo backend ao atualizar o ticket,
      // para evitar duplicar mensagem (frontend não envia aqui).

      setLoading(false);

      // Mostrar mensagem de sucesso
      toast.success('Ticket transferido com sucesso!');

      handleClose(); // Fechar o modal

      // Notificar o componente pai sobre a transferência bem-sucedida
      if (onTransferSuccess) {
        onTransferSuccess();
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth='lg' scroll='paper'>
      <form onSubmit={handleSaveTicket}>
        <DialogTitle id='form-dialog-title'>
          {i18n.t('transferTicketModal.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            style={{ width: 300, marginBottom: 20 }}
            getOptionLabel={option => `${option.name}`}
            onChange={(e, newValue) => {
              // A lista de filas é derivada (availableQueues); basta registrar
              // o usuário escolhido.
              setSelectedUser(newValue);
            }}
            options={options}
            filterOptions={filterOptions}
            freeSolo
            autoHighlight
            noOptionsText={i18n.t('transferTicketModal.noOptions')}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t('transferTicketModal.fieldLabel')}
                variant='outlined'
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <FormControl variant='outlined' className={classes.maxWidth}>
            <InputLabel>
              {i18n.t('transferTicketModal.fieldQueueLabel')}
            </InputLabel>
            <Select
              value={selectedQueue}
              onChange={e => setSelectedQueue(e.target.value)}
              label={i18n.t('transferTicketModal.fieldQueuePlaceholder')}
            >
              {availableQueues.length > 0 ? (
                availableQueues.map(queue => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value=''>
                  Nenhuma pipeline atribuída a esta conexão
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {/* CONEXAO */}
          <Grid container spacing={2} style={{ marginTop: '15px' }}>
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant='outlined'
                value={selectedWhatsapp}
                onChange={e => {
                  setSelectedWhatsapp(e.target.value);
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === '') {
                    return 'Selecione uma Conexão';
                  }
                  const whatsapp = whatsapps.find(
                    w => w.id === selectedWhatsapp
                  );
                  return whatsapp.name;
                }}
              >
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id}>
                      <ListItemText
                        primary={
                          <>
                            {/* {IconChannel(whatsapp.channel)} */}
                            <Typography
                              component='span'
                              style={{
                                fontSize: 14,
                                marginLeft: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                lineHeight: '2',
                              }}
                            >
                              {whatsapp.name} &nbsp;{' '}
                              <p
                                className={
                                  whatsapp.status === 'CONNECTED'
                                    ? classes.online
                                    : classes.offline
                                }
                              >
                                ({whatsapp.status})
                              </p>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color='secondary'
            disabled={loading}
            variant='outlined'
          >
            {i18n.t('transferTicketModal.buttons.cancel')}
          </Button>
          <ButtonWithSpinner
            variant='contained'
            type='submit'
            color='primary'
            loading={loading}
          >
            {i18n.t('transferTicketModal.buttons.ok')}
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
